import { toZonedTime } from "date-fns-tz";
import { DEFAULT_TIMEZONE } from "../config";
import { GetBookingQuery, GetLocationQuery } from "../graphql/generated";

export interface TimeSlot {
  hour: number;
  minute: number;
}

const weekdays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Convert Unix timestamp to Date object in the specified timezone
 */
export function fromUnixTimeInTimezone(
  unixTime: number,
  timezone: string
): Date {
  const utcDate = new Date(unixTime * 1000);
  return toZonedTime(utcDate, timezone);
}

/**
 * Check if two dates are the same day in the specified timezone
 */
export function isSameDayInTimezone(
  date1: Date,
  date2: Date,
  timezone: string
): boolean {
  const zonedDate1 = toZonedTime(date1, timezone);
  const zonedDate2 = toZonedTime(date2, timezone);

  return (
    zonedDate1.getFullYear() === zonedDate2.getFullYear() &&
    zonedDate1.getMonth() === zonedDate2.getMonth() &&
    zonedDate1.getDate() === zonedDate2.getDate()
  );
}

/**
 * Check if a date is today in the specified timezone
 */
export function isTodayInTimezone(date: Date, timezone: string): boolean {
  const now = new Date();
  return isSameDayInTimezone(date, now, timezone);
}

export function getTimeSlots(
  date: Date | null,
  location:
    | GetBookingQuery["booking"]["location"]
    | GetLocationQuery["location"]
): TimeSlot[] {
  if (!date || !location || location.openingHours.length === 0) {
    return [];
  }

  const restaurantTimezone = location.timezone || DEFAULT_TIMEZONE;
  const now = new Date();
  const currentTimeInRestaurantTz = toZonedTime(now, restaurantTimezone);
  const dayOfWeek = weekdays[date.getDay()].toLowerCase();

  const openingHours = location.openingHours.find(
    (ohs) => ohs.dayOfWeek.toLowerCase() === dayOfWeek
  );

  const excludedHours = location.excludedHours.filter(
    (ehs) =>
      ehs.dayOfWeek.toLowerCase() === dayOfWeek || ehs.dayOfWeek === "Everyday"
  );

  if (!openingHours) {
    return [];
  }

  const closeHour =
    openingHours.closeHour > openingHours.openHour
      ? openingHours.closeHour
      : 24;

  const timeSlots = [];
  for (let hour = openingHours.openHour; hour < closeHour; hour++) {
    // Use restaurant timezone for both isToday check and current hour comparison
    const isTodayCheck = isTodayInTimezone(date, restaurantTimezone);

    if (isTodayCheck && hour < currentTimeInRestaurantTz.getHours()) {
      continue;
    }

    if (hour > closeHour - 2) {
      continue;
    }

    let isExcludedHour = false;

    excludedHours.forEach((ehs) => {
      if (hour >= ehs.startHour && hour <= ehs.endHour) {
        isExcludedHour = true;
      }
    });

    if (isExcludedHour) {
      continue;
    }

    if (hour === openingHours.openHour) {
      timeSlots.push({
        hour,
        minute: openingHours.openMinute,
      });
      continue;
    }

    timeSlots.push({
      hour,
      minute: 0,
    });
  }

  return timeSlots;
}
